import {
    EMBED_HEIGHT_SETTLE_DEBOUNCE_MS,
    EMBED_HEIGHT_SETTLE_TIMEOUT_MS,
    EMBED_MIN_RENDER_MS,
} from './constants';
import { isValidEmbedHeight } from './helpers';

/**
 * CVT-4096: Tracks the height of a sandboxed embed across the two things the
 * sandbox tells us — a dimensions update, and the one-off confirmation that the
 * embed content is in its DOM — and decides when that height is final.
 *
 * This lives outside the Svelte component so the ordering rules below can be
 * covered by unit tests; the component only forwards the sandbox's messages and
 * renders the state it gets back.
 */
export type EmbedHeightState = {
    /** Height the sandbox iframe should be rendered at. */
    height: number;
    /** Whether the sandbox has finished measuring the embed in this page load. */
    hasMeasurementSettled: boolean;
};

export type EmbedHeightTracker = {
    /** A dimensions update posted by the sandbox. */
    reportSandboxHeight: (reportedHeight: number) => void;
    /**
     * The sandbox confirming that the embed content is in its DOM, along with
     * the height it measured at that moment.
     */
    reportInjectedContentHeight: (reportedHeight: number) => void;
    /** Clears pending timers. */
    destroy: () => void;
};

export const createEmbedHeightTracker = ({
    cachedHeight,
    onChange,
    onSettle,
}: {
    /** Height measured on a previous visit, if any. */
    cachedHeight: number | null;
    /** Called whenever the rendered state changes. */
    onChange: (state: EmbedHeightState) => void;
    /** Called with the final height once the measurement has settled. */
    onSettle: (height: number) => void;
}): EmbedHeightTracker => {
    let height = cachedHeight ?? 0;
    let hasMeasurementSettled = false;

    // Whether the sandbox has confirmed that the embed content is in its DOM.
    // Until then every reported height belongs to the sandbox's own (empty)
    // document, no matter how large or small it happens to be.
    let hasInjectedContent = false;

    // The most recent height the sandbox measured for its injected content, kept
    // independently of what is on screen: while a cached height is displayed the
    // embed is protected from shrinking, but the real value still has to be
    // remembered so settlement can adopt it.
    let latestReportedHeight: number | null = null;

    let settleDebounceTimeout: ReturnType<typeof setTimeout> | null = null;

    // CAP-4418: Whether the embed has been given long enough to actually render.
    // Until then no height may be treated as final — see `EMBED_MIN_RENDER_MS`.
    let hasMinRenderWindowElapsed = false;
    let minRenderWindowTimeout: ReturnType<typeof setTimeout> | null = null;
    let isSettleDeferred = false;

    const emitChange = () => onChange({ height, hasMeasurementSettled });

    // The reported height is only final once it stops changing — injected
    // content routinely grows a few times while a Tailwind CDN build, webfonts
    // or images land. Releasing the loading placeholder on the first report
    // would bring back the jump FUN-3908 fixed; waiting for the reports to go
    // quiet does not. The height is handed over for caching at the same moment,
    // which is what makes repeat visits shift-free.
    const scheduleSettle = () => {
        // CAP-4418: An embed whose content needs a moment to render reports the
        // same near-zero height throughout, so "stopped changing" is not on its
        // own evidence that the height is final. Hold the window open until the
        // embed has had time to render; the minimum-render timer restarts it.
        if (!hasMinRenderWindowElapsed) {
            isSettleDeferred = true;

            return;
        }

        if (settleDebounceTimeout) {
            clearTimeout(settleDebounceTimeout);
        }

        settleDebounceTimeout = setTimeout(() => {
            settleDebounceTimeout = null;
            hasMeasurementSettled = true;

            // Shrink protection ends here, so adopt the height the sandbox
            // actually reported. Without this, a cached height larger than the
            // embed's real one would stay on screen and be written straight
            // back to the cache: a height that has already stopped changing
            // produces no further dimensions update to correct it.
            if (latestReportedHeight !== null) {
                height = latestReportedHeight;
            }

            emitChange();
            onSettle(height);
        }, EMBED_HEIGHT_SETTLE_DEBOUNCE_MS);
    };

    // Failsafe: never reserve the viewport indefinitely, e.g. when the sandbox
    // host is unreachable and no dimensions are ever reported. The height is
    // deliberately not handed over for caching — it is not known to be the
    // embed's own.
    let failsafeTimeout: ReturnType<typeof setTimeout> | null = setTimeout(() => {
        failsafeTimeout = null;
        hasMeasurementSettled = true;
        emitChange();
    }, EMBED_HEIGHT_SETTLE_TIMEOUT_MS);

    const applyReportedHeight = (reportedHeight: number) => {
        latestReportedHeight = reportedHeight;

        // A height measured on a previous visit is already on screen, so a
        // repeat visitor must not watch the embed collapse and re-expand while
        // the sandbox boots: the first heights it reports are taken before
        // external CSS (a Tailwind CDN build, the normalize stylesheet) has
        // applied and can be a fraction of the real one. Until the measurement
        // has settled the embed may therefore grow past the cached height but
        // not shrink below it. Afterwards every report is applied verbatim —
        // embeds may legitimately shrink later.
        if (hasMeasurementSettled || cachedHeight === null || reportedHeight >= cachedHeight) {
            height = reportedHeight;
        }

        emitChange();
        scheduleSettle();
    };

    return {
        reportSandboxHeight: (reportedHeight) => {
            if (!isValidEmbedHeight(reportedHeight)) {
                return;
            }

            // The sandbox reports its own still-empty document before the embed
            // content is injected. Track that height so the iframe isn't left
            // at 0 if the content never arrives, but never let it clobber a
            // cached height — that would collapse the layout for a moment.
            if (!hasInjectedContent) {
                if (cachedHeight === null) {
                    height = reportedHeight;
                    emitChange();
                }

                return;
            }

            applyReportedHeight(reportedHeight);
        },

        reportInjectedContentHeight: (reportedHeight) => {
            hasInjectedContent = true;

            // CAP-4418: The content is in the DOM, so the clock on "long enough
            // to have rendered" starts here.
            if (minRenderWindowTimeout === null && !hasMinRenderWindowElapsed) {
                minRenderWindowTimeout = setTimeout(() => {
                    minRenderWindowTimeout = null;
                    hasMinRenderWindowElapsed = true;

                    if (isSettleDeferred) {
                        isSettleDeferred = false;
                        scheduleSettle();
                    }
                }, EMBED_MIN_RENDER_MS);
            }

            if (isValidEmbedHeight(reportedHeight)) {
                applyReportedHeight(reportedHeight);

                return;
            }

            // Start the settle window even without a usable height: content
            // whose height happens to match the empty sandbox document (an ~8px
            // divider) never changes the sandbox's document height and so never
            // triggers its ResizeObserver. Without this the embed would stay
            // behind the loading placeholder until the failsafe timeout.
            scheduleSettle();
        },

        destroy: () => {
            if (settleDebounceTimeout) {
                clearTimeout(settleDebounceTimeout);
            }

            if (minRenderWindowTimeout) {
                clearTimeout(minRenderWindowTimeout);
            }

            if (failsafeTimeout) {
                clearTimeout(failsafeTimeout);
            }
        },
    };
};
